/**
 * Per-user subscription management for the admin panel.
 *
 * This is the Phase 1 revenue workflow: a Tunisian customer pays cash, an admin
 * finds them here and activates their plan.
 *
 * Follows the admin panel's own conventions rather than the customer app's —
 * raw fetch + adminToken from localStorage, plain Tailwind on bare elements, no
 * Card/Dialog primitives. The one departure is that plan changes use inline
 * form panels instead of window.prompt, because they need several fields at once.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

type Panel = "activate" | "plan" | "extend" | "limits" | null;

const STATUS_TONE: Record<string, string> = {
  trial: "bg-blue-100 text-blue-700",
  active: "bg-green-100 text-green-700",
  expired: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700",
  inactive: "bg-gray-100 text-gray-700",
};

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : "—");
const fmtNum = (n: number) => n.toLocaleString("en-US");

const Pill: React.FC<{ tone?: string; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone || "bg-gray-100 text-gray-700"}`}>
    {children}
  </span>
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium">{value}</p>
  </div>
);

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [plan, setPlan] = useState("starter");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [durationMonths, setDurationMonths] = useState(1);
  const [notes, setNotes] = useState("");
  const [extendPreset, setExtendPreset] = useState("30d");
  const [customEnd, setCustomEnd] = useState("");
  const [resetUsage, setResetUsage] = useState(false);
  const [limComments, setLimComments] = useState("");
  const [limPages, setLimPages] = useState("");

  const headers = useMemo(() => {
    const token = localStorage.getItem("adminToken");
    return token
      ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      : { "Content-Type": "application/json" };
  }, []);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${userId}`, {
        headers: headers as any,
      });
      if (res.status === 401) {
        navigate("/admin/login");
        return;
      }
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [userId, headers, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const submit = async (path: string, method: string, body: any) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${userId}${path}`, {
        method,
        headers: headers as any,
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json.error || "Action failed");
        return;
      }
      setPanel(null);
      setNotes("");
      await load();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-red-600">User not found.</div>;

  const sub = data.subscription;
  const user = data.user;
  const comments = sub.usage.comments;
  const pages = sub.usage.pages;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">Subscription</h1>
          <Link to="/admin/users" className="text-blue-700 hover:underline text-sm">
            ← All users
          </Link>
        </div>

        {/* Identity */}
        <div className="bg-white border rounded p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-lg font-medium">{user.name}</span>
            <Pill tone={STATUS_TONE[sub.status]}>{sub.status}</Pill>
            {sub.isCustomLimits && <Pill tone="bg-purple-100 text-purple-700">custom limits</Pill>}
            {!sub.enforced && <Pill tone="bg-yellow-100 text-yellow-800">enforcement off</Pill>}
          </div>
          <p className="text-sm text-gray-600">
            {user.email} {user.phone ? `· ${user.phone}` : ""}
          </p>
        </div>

        {/* Current state */}
        <div className="bg-white border rounded p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <Stat label="Plan" value={sub.planName} />
            <Stat label="Payment" value={sub.paymentMethod} />
            <Stat label="Source" value={sub.subscriptionSource} />
            <Stat label="Days remaining" value={sub.daysRemaining ?? "—"} />
            <Stat label="Started" value={fmtDate(sub.startDate)} />
            <Stat label="Ends" value={fmtDate(sub.endDate)} />
            <Stat label="Period ends" value={fmtDate(sub.periodEnd)} />
            <Stat label="Trial started" value={sub.trialStarted ? "Yes" : "No"} />
          </div>

          <div className="space-y-1 text-sm">
            <p>
              <span className="text-gray-500">Comments:</span>{" "}
              {comments.limit === null
                ? `${fmtNum(comments.used)} (unlimited)`
                : `${fmtNum(comments.used)} / ${fmtNum(comments.limit)} (${comments.percent}%)`}
            </p>
            <p>
              <span className="text-gray-500">Pages:</span>{" "}
              {pages.limit === null ? `${pages.used} (unlimited)` : `${pages.used} / ${pages.limit}`}
            </p>
            {sub.moderationPaused && (
              <p className="text-red-700 font-medium">
                Moderation is PAUSED ({sub.moderationPausedReason})
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button className="px-3 py-2 bg-gray-900 text-white rounded text-sm" onClick={() => setPanel("activate")}>
            Activate plan
          </button>
          <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel("plan")}>
            Change plan
          </button>
          <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel("extend")}>
            Extend
          </button>
          <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel("limits")}>
            Custom limits
          </button>
          <button
            className="px-3 py-2 border rounded text-sm"
            onClick={() => {
              if (window.confirm("Reset this user's comment usage to zero?")) {
                submit("/reset-usage", "POST", { notes: "Admin reset usage" });
              }
            }}
          >
            Reset usage
          </button>
          <button
            className="px-3 py-2 border border-red-300 text-red-700 rounded text-sm"
            onClick={() => {
              if (window.confirm("Deactivate this subscription? Moderation stops for this user.")) {
                submit("/deactivate", "POST", { notes: "Admin deactivated" });
              }
            }}
          >
            Deactivate
          </button>
        </div>

        {panel === "activate" && (
          <div className="bg-white border rounded p-4 mb-4 space-y-3">
            <h2 className="font-medium">Activate plan</h2>
            <div className="flex flex-wrap gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Plan</span>
                <select className="border rounded px-2 py-1" value={plan} onChange={(e) => setPlan(e.target.value)}>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Payment</span>
                <select
                  className="border rounded px-2 py-1"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="manual">Manual</option>
                  <option value="online">Online</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Months</span>
                <input
                  type="number"
                  min={1}
                  className="border rounded px-2 py-1 w-20"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(Number(e.target.value))}
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="block text-xs text-gray-500">
                Payment reference / note (the only record of a cash payment)
              </span>
              <input
                className="border rounded px-2 py-1 w-full"
                placeholder="e.g. cash, receipt #142"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 bg-gray-900 text-white rounded text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => submit("/activate", "POST", { plan, paymentMethod, durationMonths, notes })}
              >
                Activate
              </button>
              <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {panel === "plan" && (
          <div className="bg-white border rounded p-4 mb-4 space-y-3">
            <h2 className="font-medium">Change plan</h2>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Plan</span>
                <select className="border rounded px-2 py-1" value={plan} onChange={(e) => setPlan(e.target.value)}>
                  <option value="free_trial">Free Trial</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              <label className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={resetUsage} onChange={(e) => setResetUsage(e.target.checked)} />
                Reset usage counters
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 bg-gray-900 text-white rounded text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => submit("/plan", "POST", { plan, resetUsage, notes })}
              >
                Change plan
              </button>
              <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {panel === "extend" && (
          <div className="bg-white border rounded p-4 mb-4 space-y-3">
            <h2 className="font-medium">Extend subscription</h2>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Preset</span>
                <select
                  className="border rounded px-2 py-1"
                  value={extendPreset}
                  onChange={(e) => setExtendPreset(e.target.value)}
                >
                  <option value="30d">+30 days</option>
                  <option value="3mo">+3 months</option>
                  <option value="1y">+1 year</option>
                  <option value="custom">Custom date…</option>
                </select>
              </label>
              {extendPreset === "custom" && (
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">New end date</span>
                  <input
                    type="date"
                    className="border rounded px-2 py-1"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </label>
              )}
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 bg-gray-900 text-white rounded text-sm disabled:opacity-50"
                disabled={busy || (extendPreset === "custom" && !customEnd)}
                onClick={() =>
                  submit(
                    "/extend",
                    "POST",
                    extendPreset === "custom" ? { endDate: customEnd, notes } : { preset: extendPreset, notes }
                  )
                }
              >
                Extend
              </button>
              <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {panel === "limits" && (
          <div className="bg-white border rounded p-4 mb-4 space-y-3">
            <h2 className="font-medium">Custom limits</h2>
            <p className="text-xs text-gray-500">
              Leave blank to keep the current value. Enter -1 for unlimited.
            </p>
            <div className="flex flex-wrap gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Comment limit</span>
                <input
                  type="number"
                  className="border rounded px-2 py-1 w-36"
                  value={limComments}
                  onChange={(e) => setLimComments(e.target.value)}
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Page limit</span>
                <input
                  type="number"
                  className="border rounded px-2 py-1 w-28"
                  value={limPages}
                  onChange={(e) => setLimPages(e.target.value)}
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 bg-gray-900 text-white rounded text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  const body: any = { notes };
                  if (limComments !== "") body.customCommentLimit = Number(limComments);
                  if (limPages !== "") body.customPageLimit = Number(limPages);
                  submit("/limits", "PATCH", body);
                }}
              >
                Save limits
              </button>
              <button
                className="px-3 py-2 border rounded text-sm"
                onClick={() => submit("/limits", "PATCH", { reset: true, notes })}
              >
                Reset to plan defaults
              </button>
              <button className="px-3 py-2 border rounded text-sm" onClick={() => setPanel(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetail;
